/* eslint-disable max-lines */
import React, { useMemo, useState } from 'react';
import { DragDropContext, DropResult, Droppable, DroppableProvided } from 'react-beautiful-dnd';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments } from '@modernloop/shared/components';
import { useFlag } from '@modernloop/shared/feature-flag';
import { PlusIcon } from '@modernloop/shared/icons';
import { Box, Button, CircularProgress, Paper, Stack, Theme, Typography } from '@mui/material';
import { clone, difference } from 'lodash';
import { v4 as uuid } from 'uuid';

import {
  AtsInterviewDefinitionFragment,
  DateTimeRangeInput,
  DurationInput,
  InterviewGroup_JobStageInterviewGroupFragment,
  InterviewGroup_JobStageInterviewsFragment,
  InterviewPlan_JobFragment,
  InterviewPlan_JobStageFragment,
  InterviewType,
  InterviewTypePrefType,
  JobStageInterview_JobStageInterviewFragment,
} from 'src/generated/mloop-graphql';

import { OrgPrefName, useOrgPrefString } from 'src/hooks/api/org';
import { useOrgAtsService } from 'src/hooks/atsService';
import { getAtsInterviewDefinitionFields } from 'src/hooks/atsService/util';
import { LoggerEvent, useLogEvent } from 'src/hooks/useLogEvent';

import { isBreak } from 'src/utils/interview';

// eslint-disable-next-line modernloop/restrict-imports.cjs
import AtsScorecardModal from 'src/views-new/Scorecards/AtsScorecardModal';
// eslint-disable-next-line modernloop/restrict-imports.cjs
import { NoScorecardOption } from 'src/entities/Ats/useAtsScorecardOptions';

import { MINUTES_IN_A_DAY, SECONDS_IN_A_DAY } from 'src/constants';
import { DEFAULT_INTERVIEW_NAME } from 'src/strings';

import AdvancedOptions from './AdvancedOptions';
import InterviewGroup from './InterviewGroup';

type Props = {
  jobStageLoading?: boolean;
  schedulable: boolean;
  readonly?: boolean;
  candidateAvailabilities?: DateTimeRangeInput[];
  footer?: React.ReactNode;
  onUpdated: (jobStage: InterviewPlan_JobStageFragment) => void;
  onResetToJobStageDefault?: () => void;
  /** True when the ATS owns the scorecard and duration for this Job Stage. */
  atsSyncedInterviewFields?: boolean;
};

type Fragments = {
  job: InterviewPlan_JobFragment | undefined;
  jobStage: InterviewPlan_JobStageFragment | undefined;
};

const useSxProps = () => {
  return useMemo(() => {
    return {
      noInterviewPlanWrapper: {
        display: 'flex',
        height: '150px',
        backgroundColor: (theme: Theme) => theme.palette.background.contrast,
        alignItems: 'center',
        justifyContent: 'center',
        margin: (theme: Theme) => `0 ${theme.spacing(3)}`,
      },
    };
  }, []);
};

const InterviewPlan: FCWithFragments<Fragments, Props> = ({
  job,
  jobStage,
  jobStageLoading,
  schedulable,
  readonly,
  candidateAvailabilities,
  footer,
  onUpdated,
  onResetToJobStageDefault,
  atsSyncedInterviewFields = false,
}): JSX.Element | null => {
  const { t } = useTranslation();
  const sxProps = useSxProps();

  const { logEvent } = useLogEvent();
  const orgBreakImprovementsEnabled = useFlag('org_break_improvements');
  const [showScorecardModal, setShowScorecardModal] = useState(false);
  const atsService = useOrgAtsService();

  const [defaultInterviewPlanBreakType, { loading: loadingDefaultInterviewPlanBreakType }] = useOrgPrefString(
    OrgPrefName.STR_DEFAULT_INTERVIEW_PLAN_BREAK_TYPE
  );

  const interviewPlanBreakType = [InterviewTypePrefType.AnyTimeLater, InterviewTypePrefType.ImmediatelyAfter].includes(
    defaultInterviewPlanBreakType as InterviewTypePrefType
  )
    ? (defaultInterviewPlanBreakType as InterviewTypePrefType)
    : InterviewTypePrefType.AnyTimeLater;

  const handleSelectScorecard = (scorecard: AtsInterviewDefinitionFragment) => {
    if (!jobStage) {
      return;
    }

    const interview: JobStageInterview_JobStageInterviewFragment = {
      id: uuid(),
      name: scorecard.name || DEFAULT_INTERVIEW_NAME,
      interviewType: InterviewType.Interview,
      isLockedOrder: false,
      isHiddenFromCandidate: false,
      atsInterviewDefinition: {
        atsId: scorecard.atsId,
        atsJobId: scorecard.atsJobId,
        name: scorecard.name || DEFAULT_INTERVIEW_NAME,
      },
      duration: getAtsInterviewDefinitionFields(scorecard.atsFields || undefined, 'estimatedMinutes') || 45,
    };

    const interviewGroup: InterviewGroup_JobStageInterviewGroupFragment = {
      id: uuid(),
      locked: false,
      jobStageInterviews: [interview],
    };

    const interviewGroups: InterviewGroup_JobStageInterviewGroupFragment[] = [interviewGroup];

    if (orgBreakImprovementsEnabled && jobStage.jobStageInterviewGroups?.length) {
      const breakSlot: JobStageInterview_JobStageInterviewFragment = {
        id: uuid(),
        isLockedOrder: true,
        name: 'Break',
        interviewType:
          interviewPlanBreakType === InterviewTypePrefType.AnyTimeLater
            ? InterviewType.AnyTimeLater
            : InterviewType.Break,
        duration: 0,
        isHiddenFromCandidate: false,
      };

      const breakGroup: InterviewGroup_JobStageInterviewGroupFragment = {
        id: uuid(),
        locked: true,
        jobStageInterviews: [breakSlot],
      };

      interviewGroups.unshift(breakGroup);
    }

    onUpdated({
      ...jobStage,
      jobStageInterviewGroups: [...(jobStage?.jobStageInterviewGroups || []), ...interviewGroups],
    });
  };

  const handleAddBreak = () => {
    if (!jobStage) {
      return;
    }

    const breakInterview: JobStageInterview_JobStageInterviewFragment = {
      id: uuid(),
      name: 'Break',
      interviewType: InterviewType.Break,
      isLockedOrder: true,
      isHiddenFromCandidate: false,
      duration: 45,
    };

    const breakGroup: InterviewGroup_JobStageInterviewGroupFragment = {
      id: uuid(),
      locked: true, // Break groups are always locked while adding
      jobStageInterviews: [breakInterview],
    };

    onUpdated({
      ...jobStage,
      jobStageInterviewGroups: [...(jobStage?.jobStageInterviewGroups || []), breakGroup],
    });
  };

  const handleAddInterview = () => {
    if (atsService?.hasScorecards) {
      setShowScorecardModal(true);
      return;
    }

    handleSelectScorecard(NoScorecardOption);
  };

  const handleScorecardSelectClose = () => {
    setShowScorecardModal(false);
  };

  const handleMoveJobStageInterviewGroup = (groupId: string, position: number) => {
    if (!jobStage?.jobStageInterviewGroups) {
      return;
    }

    const groupIndex = jobStage.jobStageInterviewGroups.findIndex((group) => group.id === groupId);
    if (groupIndex === undefined || groupIndex === -1) {
      return;
    }

    const newJobStageInterviewGroups = Array.from(jobStage.jobStageInterviewGroups || []);

    if (orgBreakImprovementsEnabled) {
      // Swap the interview at groupIndex with the interview at position
      const tempGroup = newJobStageInterviewGroups[groupIndex];
      newJobStageInterviewGroups[groupIndex] = newJobStageInterviewGroups[position];
      newJobStageInterviewGroups[position] = tempGroup;
    } else {
      const group = newJobStageInterviewGroups.splice(groupIndex, 1) || [];
      newJobStageInterviewGroups.splice(position, 0, ...group);
    }

    onUpdated({
      ...jobStage,
      jobStageInterviewGroups: newJobStageInterviewGroups,
    });
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || !jobStage?.jobStageInterviewGroups) {
      return;
    }

    if (orgBreakImprovementsEnabled) {
      const destinationGroup =
        jobStage.jobStageInterviewGroups.length > result.destination.index
          ? jobStage.jobStageInterviewGroups[result.destination.index]
          : undefined;

      if (!destinationGroup) {
        return;
      }

      // If the destination group is a break group don't allow moving
      if (
        destinationGroup.jobStageInterviews?.length === 1 &&
        isBreak(destinationGroup.jobStageInterviews[0].interviewType)
      ) {
        return;
      }

      handleMoveJobStageInterviewGroup(result.draggableId, result.destination.index);
    } else {
      const groupIndex = jobStage.jobStageInterviewGroups.findIndex((group) => group.id === result.draggableId);

      if (groupIndex === -1) {
        return;
      }
      const newJobStageInterviewGroups = Array.from(jobStage.jobStageInterviewGroups || []);

      const group = newJobStageInterviewGroups.splice(groupIndex, 1) || [];
      newJobStageInterviewGroups.splice(result.destination.index, 0, ...group);

      onUpdated({
        ...jobStage,
        jobStageInterviewGroups: newJobStageInterviewGroups,
      });
    }
  };

  // Merge all the interviews from startGroupIndex to endGroupIndex into startGroupIndex
  const handleGroupJobStageInterviewGroup = (startGroupIndex: number, endGroupIndex: number) => {
    if (!jobStage?.jobStageInterviewGroups) {
      return;
    }

    // We want to keep visual order while grouping interviews so smaller index is always destination and delete the source.
    // Other way around will work too 🙂.
    let startIndex = startGroupIndex;
    let endIndex = endGroupIndex;

    if (startIndex > endIndex) {
      startIndex = endGroupIndex;
      endIndex = startGroupIndex;
    }

    const newJobStageInterviewGroups = Array.from(jobStage.jobStageInterviewGroups || []);
    const groupsToMerge = newJobStageInterviewGroups.splice(startIndex + 1, endIndex - startIndex);

    if (groupsToMerge.length === 0) {
      return;
    }

    groupsToMerge.forEach((group) => {
      group.jobStageInterviews?.forEach((interview) => {
        newJobStageInterviewGroups[startIndex].jobStageInterviews?.push(interview);
      });
    });

    onUpdated({
      ...jobStage,
      jobStageInterviewGroups: newJobStageInterviewGroups,
    });
  };

  const handleUngroupJobStageInterviewGroup = (groupId: string) => {
    if (!jobStage || !jobStage.jobStageInterviewGroups) {
      return;
    }

    const groupIndex = jobStage.jobStageInterviewGroups.findIndex((group) => group.id === groupId);
    if (groupIndex === undefined || groupIndex === -1) {
      return;
    }

    const newGroups: InterviewGroup_JobStageInterviewGroupFragment[] = [];

    const group = jobStage.jobStageInterviewGroups[groupIndex];

    group.jobStageInterviews?.forEach((interview) => {
      if (!interview) {
        return;
      }

      newGroups.push({
        id: uuid(),
        locked: interview.isLockedOrder,
        jobStageInterviews: [interview],
      });
    });

    const resultGroups = [...jobStage.jobStageInterviewGroups];
    resultGroups.splice(groupIndex, 1, ...newGroups);
    onUpdated({
      ...jobStage,
      jobStageInterviewGroups: resultGroups,
    });
  };

  const jobStageInterviewGroups = jobStage?.jobStageInterviewGroups?.flat() || [];

  const jobStageInterviews = useMemo(() => {
    return (
      jobStage?.jobStageInterviewGroups
        ?.map((group) => group.jobStageInterviews as InterviewGroup_JobStageInterviewsFragment[])
        .flat() || []
    );
  }, [jobStage?.jobStageInterviewGroups]);

  const minSchedulingWindowSecondsNeeded = useMemo(() => {
    const numberOfDayBreaks = jobStageInterviews.reduce((acc, jobStageInterview) => {
      if (
        jobStageInterview.interviewType === InterviewType.DayDivider ||
        (jobStageInterview.interviewType === InterviewType.Break && jobStageInterview.duration === MINUTES_IN_A_DAY) ||
        jobStageInterview.interviewType === InterviewType.NextDay
      ) {
        return acc + 1;
      }
      return acc;
    }, 0);

    return (numberOfDayBreaks + 1) * SECONDS_IN_A_DAY;
  }, [jobStageInterviews]);

  if (jobStageLoading) {
    return (
      <Box mt={4}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  const addInterviewButton = (
    <Box m="0 auto" position="relative">
      <Button
        variant="contained"
        size="medium"
        disabled={loadingDefaultInterviewPlanBreakType}
        startIcon={<PlusIcon />}
        onClick={handleAddInterview}
      >
        {t('hiring-dashboard-interviewers:interviewers.plan.addInterview')}
      </Button>
    </Box>
  );

  const left = (
    <Stack spacing={2} flexWrap="nowrap">
      {jobStageInterviewGroups.length === 0 && (
        <Paper variant="outlined" sx={sxProps.noInterviewPlanWrapper}>
          <Stack alignItems="center" gap={1}>
            <Typography color="theme.secondary" variant="body2" fontWeight={500}>
              {t('hiring-dashboard-interviewers:interviewers.plan.emptyState')}
            </Typography>
            {orgBreakImprovementsEnabled && addInterviewButton}
          </Stack>
        </Paper>
      )}

      {jobStageInterviewGroups.length > 0 && job && (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="droppable" direction="vertical" isDropDisabled={readonly}>
            {(droppableProvided: DroppableProvided) => (
              <Stack
                spacing={orgBreakImprovementsEnabled ? 0 : 2}
                ref={droppableProvided.innerRef}
                {...droppableProvided.droppableProps}
              >
                {jobStage?.jobStageInterviewGroups?.map((group, i) => {
                  return (
                    <InterviewGroup
                      job={job}
                      jobStageInterviewGroup={group}
                      jobStageInterviewGroups={jobStageInterviewGroups}
                      jobStageInterviews={jobStageInterviews}
                      count={jobStageInterviewGroups.length}
                      schedulingWindow={jobStage.schedulingWindow || undefined}
                      key={group.id}
                      index={i}
                      schedulable={schedulable}
                      readonly={readonly}
                      candidateAvailabilities={candidateAvailabilities}
                      minSchedulingWindowSecondsNeeded={minSchedulingWindowSecondsNeeded}
                      atsSyncedInterviewFields={atsSyncedInterviewFields}
                      onUpdated={(updatedGroup, schedulingWindowDuration?: DurationInput) => {
                        const newSeatIds =
                          updatedGroup.jobStageInterviews
                            ?.map((interview) => interview.jobStageInterviewSeats?.map((seat) => seat.id).flat() || [])
                            .flat() || [];

                        const oldSeatIds =
                          group.jobStageInterviews
                            ?.map((interview) => interview.jobStageInterviewSeats?.map((seat) => seat.id).flat() || [])
                            .flat() || [];

                        const deletedSeatIds = difference(oldSeatIds, newSeatIds);

                        const updatedJobStage: InterviewPlan_JobStageFragment = {
                          ...jobStage,
                          jobStageInterviewGroups: jobStageInterviewGroups.map((g) => {
                            if (!deletedSeatIds.length) {
                              if (g.id === updatedGroup.id) {
                                return updatedGroup;
                              }
                              return g;
                            }

                            const groupClone = g.id === updatedGroup.id ? clone(updatedGroup) : clone(g);
                            groupClone.jobStageInterviews = groupClone.jobStageInterviews?.map((interview) => {
                              interview.jobStageInterviewSeats = interview.jobStageInterviewSeats?.filter((seat) => {
                                if (!seat.linkedSeat) {
                                  return true;
                                }
                                return !deletedSeatIds.includes(seat.linkedSeat.linkedJobStageInterviewSeatId);
                              });
                              return interview;
                            });
                            return groupClone;
                          }),
                          schedulingWindow: schedulingWindowDuration || jobStage.schedulingWindow,
                        };
                        onUpdated(updatedJobStage);
                      }}
                      onDelete={(jobStageInterviewGroupId) => {
                        const deletedSeatIds =
                          jobStageInterviewGroups
                            .find((g) => g.id === jobStageInterviewGroupId)
                            ?.jobStageInterviews?.map((interview) =>
                              interview.jobStageInterviewSeats?.map((seat) => seat.id).flat()
                            )
                            .flat() || [];

                        const jobStageInterviewGroupsClone = Array.from(jobStageInterviewGroups);

                        if (orgBreakImprovementsEnabled) {
                          if (i === jobStageInterviewGroupsClone.length - 1) {
                            // If the last group is being deleted, we need to delete the break group above it.
                            jobStageInterviewGroupsClone.splice(i - 1, 2);
                          } else {
                            jobStageInterviewGroupsClone.splice(i, 2);
                          }
                        } else {
                          jobStageInterviewGroupsClone.splice(i, 1);
                        }

                        onUpdated({
                          ...jobStage,
                          jobStageInterviewGroups: jobStageInterviewGroupsClone.map((g) => {
                            const groupClone = clone(g);
                            groupClone.jobStageInterviews = groupClone.jobStageInterviews?.map((interview) => {
                              interview.jobStageInterviewSeats = interview.jobStageInterviewSeats?.filter((seat) => {
                                if (!seat.linkedSeat) {
                                  return true;
                                }
                                return !deletedSeatIds.includes(seat.linkedSeat.linkedJobStageInterviewSeatId);
                              });
                              return interview;
                            });
                            return groupClone;
                          }),
                        });
                      }}
                      onMoveJobStageInterviewGroup={handleMoveJobStageInterviewGroup}
                      onGroupJobStageInterviewGroup={handleGroupJobStageInterviewGroup}
                      onUngroupJobStageInterviewGroup={handleUngroupJobStageInterviewGroup}
                    />
                  );
                })}
                {!readonly && orgBreakImprovementsEnabled && addInterviewButton}
                {droppableProvided.placeholder}
              </Stack>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {!readonly && !orgBreakImprovementsEnabled && (
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" justifyContent="space-between" rowGap={1} width="100%" flexWrap="wrap">
            <Stack direction="row" spacing={2}>
              <Button variant="contained" size="medium" onClick={handleAddInterview}>
                {t('hiring-dashboard-interviewers:interviewers.plan.addInterview')}
              </Button>
              <Button size="medium" onClick={handleAddBreak}>
                {t('hiring-dashboard-interviewers:interviewers.plan.addBreak')}
              </Button>
            </Stack>
            {onResetToJobStageDefault && (
              <Button
                variant="text"
                size="medium"
                color="primary"
                onClick={() => {
                  logEvent(LoggerEvent.CLIENT_RESET_JOB_STAGE_PLAN, {
                    type: 'Interview Plan - Reset interview plan to job stage default',
                  });
                  onResetToJobStageDefault();
                }}
              >
                {t('hiring-dashboard-interviewers:interviewers.plan.resetToDefault')}
              </Button>
            )}
          </Stack>
        </Stack>
      )}

      <AdvancedOptions
        readonly={readonly}
        minSchedulingWindowSecondsNeeded={minSchedulingWindowSecondsNeeded}
        interviewPlan={jobStage}
        onSchedulingWindowChange={(duration: DurationInput) => {
          if (!jobStage) {
            return;
          }
          onUpdated({ ...jobStage, schedulingWindow: { seconds: duration.seconds } });
        }}
        onExcludedInterviewerChange={(excludedEmployeeIds) => {
          if (!jobStage) {
            return;
          }
          onUpdated({ ...jobStage, excludedEmployees: excludedEmployeeIds.map((id) => ({ id })) });
        }}
        onResetToJobStageDefault={onResetToJobStageDefault}
      />

      {footer}
    </Stack>
  );

  return (
    <>
      {left}
      {showScorecardModal && job?.atsId ? (
        <AtsScorecardModal
          atsJobId={job?.atsId}
          onClose={handleScorecardSelectClose}
          onSelectScorecard={handleSelectScorecard}
        />
      ) : null}
    </>
  );
};

InterviewPlan.fragments = {
  job: gql`
    ${InterviewGroup.fragments.job}
    fragment InterviewPlan_job on Job {
      id
      atsId
      ...InterviewGroup_job
    }
  `,
  jobStage: gql`
    ${AdvancedOptions.fragments.interviewPlan}
    ${InterviewGroup.fragments.jobStageInterviewGroup}
    ${InterviewGroup.fragments.jobStageInterviews}
    ${InterviewGroup.fragments.jobStageInterviewGroups}
    ${InterviewGroup.fragments.schedulingWindow}

    fragment InterviewPlan_jobStage on JobStage {
      id
      jobStageInterviewGroups {
        id
        ...InterviewGroup_jobStageInterviewGroup
        ...InterviewGroup_jobStageInterviewGroups
        jobStageInterviews {
          id
          ...InterviewGroup_jobStageInterviews
        }
      }
      ...AdvancedOptions_interviewPlan
      schedulingWindow {
        ...InterviewGroup_schedulingWindow
      }
    }
  `,
};

export default InterviewPlan;
